import React, { useEffect, useMemo, useState, useRef } from 'react';
import { computeChart } from './qimen/engine.js';
import PalaceCell from './components/PalaceCell.jsx';
import PalaceModal from './components/PalaceModal.jsx';
import Legend from './components/Legend.jsx';
import QimenAnalysis from './components/QimenAnalysis.jsx';
import { crossReading } from './qimen/analysis.js';
import './qimen-analysis.css';
// [MHQI] 集成奇门 AI 求测与解读（梅花 AI 下行同步引入）
import QimenAiChat from '../../components/QimenAiChat';

// 九宫空间排布（洛书方位：上南下北，左东右西）
const GRID_ORDER = [4, 9, 2, 3, 5, 7, 8, 1, 6];



const pad = (n) => String(n).padStart(2, '0');

function nowForm() {
  const d = new Date();
  return {
    year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate(),
    hour: d.getHours(), minute: d.getMinutes(),
  };
}

export default function QimenPage() {
  const [form, setForm] = useState(nowForm);
  const [shenMode, setShenMode] = useState('nine');   // 默认九神盘（市售阴盘 APK 法度）| yin=一律顺布 | classic=阳顺阴逆
  const [juMethod, setJuMethod] = useState('zhishu'); // 默认支数和定局（市售阴盘 APK 法）| chaibu=节气拆补
  const [chart, setChart] = useState(null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  // 判读只在用户提问之后出现：{ question, category }；盘面本身保持原样
  const [asked, setAsked] = useState(null);

  const compute = (f = form, mode = shenMode, jm = juMethod) => {
    try {
      setError('');
      setChart(computeChart({ ...f, shenMode: mode, juMethod: jm }));
    } catch (err) {
      setError(err.message || String(err));
    }
  };

  // 首屏即排当前时辰
  useEffect(() => { compute(); }, []);

  const change = (e) => {
    const { name, value } = e.target;
    const f = { ...form, [name]: value === '' ? '' : parseInt(value, 10) };
    setForm(f);
  };

  const switchMode = (mode) => {
    setShenMode(mode);
    if (chart) compute(form, mode, juMethod);
  };

  const switchJuMethod = (jm) => {
    setJuMethod(jm);
    if (chart) compute(form, shenMode, jm);
  };

  const palaceOf = useMemo(() =>
    Object.fromEntries((chart?.palaces || []).map(p => [p.num, p])), [chart]);

  // 按用户所问之事计算交叉关系（问什么，就断什么）
  const reading = useMemo(
    () => (chart && asked ? crossReading(chart, asked.category) : null),
    [chart, asked]
  );

  // [MHQI] 从历史页「继续记录」交接进入：以快照重建此局（盘法一并还原，仅一次）
  const restoredOnceRef = useRef(false);
  useEffect(() => {
    if (restoredOnceRef.current) return;
    restoredOnceRef.current = true;
    try {
      const raw = localStorage.getItem('qm-ai-restore');
      if (!raw) return;
      localStorage.removeItem('qm-ai-restore');
      const snap = JSON.parse(raw || 'null');
      const c = snap?.chart;
      const anchor = c?.input || {};
      if (!anchor.year || !anchor.hour || !Array.isArray(c?.palaces)) return;
      const f = {
        ...nowForm(),
        year: anchor.year, month: anchor.month, day: anchor.day,
        hour: anchor.hour, minute: anchor.minute ?? 0,
      };
      const mode = anchor.shenMode || 'nine';
      const jm = anchor.juMethod || 'zhishu';
      setForm(f);
      setShenMode(mode);
      setJuMethod(jm);
      setChart(computeChart({ ...f, shenMode: mode, juMethod: jm }));
    } catch {
      /* 忽略非法快照 */
    }
  }, []);

  return (
    <div className="app">
      <header className="app-header">
        <div className="page-eyebrow">奇门遁甲 · 阴盘转盘</div>
        <h1>奇门排盘</h1>
        <p className="subtitle">转盘时家 · 拆补定元 · 神盘顺布 —— 天地人神四盘全息</p>
      </header>

      {/* ---------- 控制区 ---------- */}
      <section className="panel controls">
        <div className="fields">
          {[
            ['year', '年', 1900, 2100], ['month', '月', 1, 12], ['day', '日', 1, 31],
            ['hour', '时', 0, 23], ['minute', '分', 0, 59],
          ].map(([name, label, min, max]) => (
            <label key={name}>
              <span>{label}</span>
              <input name={name} type="number" min={min} max={max}
                value={form[name]} onChange={change} />
            </label>
          ))}
        </div>
        <div className="actions">
          <button className="btn ghost" onClick={() => { const f = nowForm(); setForm(f); compute(f); }}>现在</button>
          <button className="btn primary" onClick={() => compute()}>排 盘</button>
        </div>
        <div className="mode-switch" role="radiogroup" aria-label="定局法">
          <span className="mode-label">定局法</span>
          <button className={juMethod === 'zhishu' ? 'seg on' : 'seg'}
            onClick={() => switchJuMethod('zhishu')}>支数和（阴盘 · 默认）</button>
          <button className={juMethod === 'chaibu' ? 'seg on' : 'seg'}
            onClick={() => switchJuMethod('chaibu')}>节气拆补（对照）</button>
        </div>
        <div className="mode-switch" role="radiogroup" aria-label="八神布法">
          <span className="mode-label">八神布法</span>
          <button className={shenMode === 'nine' ? 'seg on' : 'seg'}
            onClick={() => switchMode('nine')}>九神盘（阴盘 · 默认）</button>
          <button className={shenMode === 'yin' ? 'seg on' : 'seg'}
            onClick={() => switchMode('yin')}>八神 · 一律顺布</button>
          <button className={shenMode === 'classic' ? 'seg on' : 'seg'}
            onClick={() => switchMode('classic')}>传统阳盘 · 阳顺阴逆</button>
        </div>
      </section>

      {error && <p className="error">⚠ {error}</p>}

      {chart && (
        <>
          {/* ---------- 盘面信息 ---------- */}
          <section className="summary" id="qimen-chart">
            <div className="sum-group">
              <h4>历法</h4>
              <div className="sum-items">
                <div className="sum-item"><i>公历</i><b>{chart.solarText.slice(0, 16)}</b></div>
                <div className="sum-item pillars">
                  <i>四柱</i>
                  <b className="gz">
                    {[['year', '年'], ['month', '月'], ['day', '日'], ['hour', '时']].map(([k, zh]) => (
                      <span key={k} className="pillar">
                        <span className="pillar-gz">{chart.fourPillars[k]}</span>
                        <span className="pillar-zh">{zh}</span>
                      </span>
                    ))}
                  </b>
                </div>
                <div className="sum-item"><i>节气</i><b>{chart.jieqi}{chart.yuan === '支数' ? ' · 支数定局' : ` · ${chart.yuan}元（符头 ${chart.fuTou}）`}</b></div>
              </div>
            </div>
            <div className="sum-group hl">
              <h4>局象</h4>
              <div className="sum-items">
                <div className="sum-item ju"><b>{chart.yinYang}遁 {chart.ju} 局</b></div>
                <div className="sum-item"><i>旬首</i><b>{chart.xunShou}（遁{chart.xunShouYi}）</b></div>
                <div className="sum-item"><i>值符</i><b className="gold">{chart.zhiFu}</b></div>
                <div className="sum-item"><i>值使</i><b className="green">{chart.zhiShi}</b></div>
              </div>
            </div>
            <div className="sum-group">
              <h4>神煞</h4>
              <div className="sum-items">
                <div className="sum-item"><i>时空</i><b className="purple">{chart.shiKong.join('')} <small style={{ color: '#999' }}>○</small></b></div>
                <div className="sum-item"><i>马星</i><b>{chart.maXing.zhi}（{chart.dayMa}）</b></div>
              </div>
            </div>
          </section>

          {/* ---------- 九宫盘面 ---------- */}
          <section className="board">
            <div className="compass top">南 · 离</div>
            <div className="compass bottom">北 · 坎</div>
            <div className="compass left">东 · 震</div>
            <div className="compass right">西 · 兑</div>
            <div className="grid">
              {GRID_ORDER.map(num => (
                <PalaceCell key={num} p={palaceOf[num]} onSelect={setSelected} />
              ))}
            </div>
          </section>

          <Legend shenMode={shenMode} />

          <footer className="note">
            起局规则：冬至后阳遁、夏至后阴遁；
            {juMethod === 'zhishu'
              ? '定局用「年支数+农历月+农历日+时支数」之和 mod 9（余0作9，市售阴盘 APK 支数法）；'
              : '以节气定三元局数，以日柱符头（甲己日）定上中下元（拆补法，不置闰）；'}
            旬首取时柱六甲；九星八门沿八宫环序转盘，天禽寄坤二随天芮；值使门自本宫按时辰飞宫（落中五寄坤二）；
            神盘{shenMode === 'yin' ? '不分阴阳遁一律顺布（阴盘法度）'
              : shenMode === 'classic' ? '阳遁顺布、阴遁逆布（传统阳盘法度）'
                : '阳顺阴逆、太常居中五（九神盘）'}。
            四害＝空亡（日/时旬空支落宫）、门迫（门克宫）、击刑（六仪落刑宫）、入墓（干/门/星落墓库）。
            排盘仅供研究参考，玄学内容请理性看待。
          </footer>
        </>
      )}

      {/* [MHQI] 奇门 AI 求测与解读（跟随当前盘面，随盘式变化重新起会话） */}
      {chart && (
        <div className="qm-ai-host">
          <QimenAiChat
            chart={chart}
            analysis={reading || (chart ? crossReading(chart, asked?.category || 'general') : null)}
            onAsked={({ question, category }) => setAsked({ question, category })}
          />

          {/* 提问之后才出现的判读：关系图与依据都在这里，盘面本身不动 */}
          {asked && reading && (
            <QimenAnalysis
              chart={chart}
              category={asked.category}
              question={asked.question}
              focus={selected?.num || null}
            />
          )}
        </div>
      )}

      <PalaceModal p={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
